import { CarOutlined, TeamOutlined } from "@ant-design/icons";
import { Card, Col, Row, Skeleton, Typography } from "antd";
import type { ReactNode } from "react";
import type { DashboardData } from "@/app/api/types";

const { Text, Title } = Typography;

interface DashboardStatsProps {
	data?: DashboardData;
	loading?: boolean;
}

type Tone = "info" | "ink" | "success" | "warning";

const DOT_CLASS: Record<Tone, string> = {
	info: "bg-info",
	ink: "bg-neutral-500",
	success: "bg-success",
	warning: "bg-warning",
};

/** A single headline KPI in the "Maydon, hozir" band: signal dot + label + big number. */
function Kpi({ tone, label, value }: { tone: Tone; label: string; value: number }) {
	return (
		<div>
			<div className="flex items-center gap-2 text-ink-soft text-sm">
				<span className={`inline-block size-2 rounded-full ${DOT_CLASS[tone]}`} />
				{label}
			</div>
			<div className="font-display text-3xl text-ink mt-1 tabular-nums">{value}</div>
		</div>
	);
}

/** A labelled sub-metric inside a detail card (mono time label + colored count). */
function SubMetric({
	label,
	value,
	valueClass,
	span = 8,
}: {
	label: ReactNode;
	value: number;
	valueClass: string;
	span?: number;
}) {
	return (
		<Col span={span}>
			<Text type="secondary" className="block mb-1 font-medium">
				{label}
			</Text>
			<div className={`text-2xl font-semibold tabular-nums ${valueClass}`}>{value}</div>
		</Col>
	);
}

export function DashboardStats({ data, loading }: DashboardStatsProps) {
	if (!(data || loading)) {
		return null;
	}

	const staffTotal = data?.today?.staffTotal ?? 0;
	const equipTotal = data?.equipment?.total ?? 0;
	const online = data?.equipment?.online ?? 0;
	const offline = data?.equipment?.offline ?? 0;
	const withGps = data?.equipment?.withGpsDevice ?? 0;
	const withoutGps = Math.max(equipTotal - withGps, 0);

	return (
		<div className="space-y-4">
			{/* "Maydon, hozir" — live field operations band (signature spine + display numbers). */}
			<div className="bg-white rounded-lg border-l-4 border-primary shadow-card px-5 py-4">
				<div className="flex items-center gap-2 mb-4">
					<span className="font-display text-lg text-ink">Maydon, hozir</span>
					<span className="inline-flex items-center gap-1.5 text-xs text-success font-medium">
						<span className="inline-block size-2 rounded-full bg-success animate-pulse" />
						Jonli
					</span>
				</div>
				{loading ? (
					<Skeleton active paragraph={{ rows: 1 }} title={false} />
				) : (
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						<Kpi tone="info" label="Xodimlar" value={staffTotal} />
						<Kpi tone="ink" label="Texnika" value={equipTotal} />
						<Kpi tone="success" label="Online" value={online} />
						<Kpi tone="warning" label="Offline" value={offline} />
					</div>
				)}
			</div>

			{/* Detail cards: unique breakdowns only — headline totals live in the band above. */}
			<Row gutter={[16, 16]}>
				<Col xs={24} lg={12}>
					<Card loading={loading} className="h-full shadow-card pt-2">
						<div className="flex items-center gap-2 mb-4">
							<TeamOutlined className="text-info text-xl" />
							<Title level={4} style={{ margin: 0 }}>
								Bugungi davomat
							</Title>
						</div>
						<Row gutter={[16, 16]}>
							<SubMetric
								label={<span className="font-mono">09:00–10:00</span>}
								value={data?.today?.attendance?.morning ?? 0}
								valueClass="text-info"
							/>
							<SubMetric
								label={<span className="font-mono">14:00–15:00</span>}
								value={data?.today?.attendance?.noon ?? 0}
								valueClass="text-success"
							/>
							<SubMetric
								label={<span className="font-mono">17:00–18:00</span>}
								value={data?.today?.attendance?.night ?? 0}
								valueClass="text-warning"
							/>
						</Row>
					</Card>
				</Col>

				<Col xs={24} lg={12}>
					<Card loading={loading} className="h-full shadow-card pt-2">
						<div className="flex items-center gap-2 mb-4">
							<CarOutlined className="text-warning text-xl" />
							<Title level={4} style={{ margin: 0 }}>
								GPS qamrovi
							</Title>
						</div>
						<Row gutter={[16, 16]}>
							<SubMetric label="O'rnatilgan" value={withGps} valueClass="text-success" span={12} />
							<SubMetric label="GPS yo'q" value={withoutGps} valueClass="text-ink-soft" span={12} />
						</Row>
					</Card>
				</Col>
			</Row>
		</div>
	);
}
